import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { searchPodcasts } from '../lib/podcastIndex'
import type { Creator, Episode } from '../lib/types'
import { BrandStar } from './BrandStar'
import { SiteIcon } from './SiteIcon'
import { Button } from './ui/button'
import { Input } from './ui/input'

type SearchBarProps = {
  defaultValue?: string
  large?: boolean
  autoFocus?: boolean
  onSearch?: (value: string) => void
  mode?: 'auto' | 'content' | 'personalities' | 'all'
}

type SearchHit =
  | { id: string; label: string; meta: string; href: string; kind: 'creator' }
  | { id: string; label: string; meta: string; href: string; kind: 'podcast' }
  | { id: string; label: string; meta: string; href: string; kind: 'episode' }

export function SearchBar({ defaultValue, large = false, autoFocus = true, onSearch, mode = 'auto' }: SearchBarProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const queryFromUrl = new URLSearchParams(location.search).get('q') ?? ''
  const resolvedMode =
    mode === 'auto'
      ? location.pathname === '/personality-records'
        ? 'personalities'
        : location.pathname === '/browse'
          ? 'content'
          : 'all'
      : mode
  const initialValue = defaultValue ?? queryFromUrl

  const [query, setQuery] = useState(initialValue)
  const [hits, setHits] = useState<SearchHit[]>([])
  const [open, setOpen] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setQuery(initialValue)
  }, [initialValue])

  useEffect(() => {
    if (!autoFocus) return
    const timer = window.setTimeout(() => inputRef.current?.focus(), 80)
    return () => window.clearTimeout(timer)
  }, [autoFocus])

  useEffect(() => {
    let cancelled = false
    const trimmed = query.trim()
    if (!trimmed) {
      setHits([])
      setSearchError(null)
      return
    }

    const timer = window.setTimeout(async () => {
      try {
        const promises: Promise<unknown>[] = []

        if (resolvedMode !== 'personalities') {
          promises.push(searchPodcasts(trimmed, 4))
        }
        if (resolvedMode !== 'personalities') {
          promises.push(
            supabase.from('episodes').select('id,title,external_url,tags').ilike('title', `%${trimmed}%`).limit(4) as unknown as Promise<unknown>
          )
        }
        if (resolvedMode !== 'content') {
          promises.push(
            supabase.from('creators').select('id,name,slug,type,tags').ilike('name', `%${trimmed}%`).limit(4) as unknown as Promise<unknown>
          )
        }

        if (resolvedMode === 'personalities') {
          const results = await Promise.all([
            supabase.from('creators').select('id,name,slug,type,tags').ilike('name', `%${trimmed}%`).limit(6)
          ])
          if (cancelled) return
          const creatorHits = ((results[0]?.data ?? []) as Pick<Creator, 'id' | 'name' | 'slug' | 'type' | 'tags'>[]).map((creator) => ({
            id: creator.id,
            label: creator.name,
            meta: `${creator.type.replace('_', ' ')}${creator.tags?.[0] ? ` · ${creator.tags[0]}` : ''}`,
            href: `/creators/${creator.slug}`,
            kind: 'creator' as const,
          }))
          setHits(creatorHits)
          setOpen(true)
          return
        }

        const results = await Promise.all(promises)
        if (cancelled) return
        setSearchError(null)

        const podcastFeeds = results[0] as Awaited<ReturnType<typeof searchPodcasts>>
        const episodeData = (results[1] as { data?: unknown[] })?.data ?? []
        const creatorData = results.length > 2 ? (results[2] as { data?: unknown[] })?.data ?? [] : []

        const podcastHits = podcastFeeds.slice(0, 4).map((feed) => ({
          id: String(feed.id),
          label: feed.title || 'Untitled podcast',
          meta: `Podcast${feed.author ? ` · ${feed.author}` : ''}`,
          href: `/podcasts/${feed.id}`,
          kind: 'podcast' as const,
        }))

        const episodeHits = (episodeData as Pick<Episode, 'id' | 'title' | 'external_url' | 'tags'>[]).map((episode) => ({
          id: episode.id,
          label: episode.title,
          meta: `Episode${episode.tags?.[0] ? ` · ${episode.tags[0]}` : ''}`,
          href: episode.external_url ?? `/browse?q=${encodeURIComponent(trimmed)}`,
          kind: 'episode' as const,
        }))

        let creatorHits: SearchHit[] = []
        if (resolvedMode !== 'content') {
          creatorHits = (creatorData as Pick<Creator, 'id' | 'name' | 'slug' | 'type' | 'tags'>[]).map((creator) => ({
            id: creator.id,
            label: creator.name,
            meta: `${creator.type.replace('_', ' ')}${creator.tags?.[0] ? ` · ${creator.tags[0]}` : ''}`,
            href: `/creators/${creator.slug}`,
            kind: 'creator' as const,
          }))
        }

        setHits(resolvedMode === 'content' ? [...podcastHits, ...episodeHits] : [...creatorHits, ...podcastHits, ...episodeHits])
        setOpen(true)
      } catch (err) {
        if (!cancelled) {
          setSearchError(err instanceof Error ? err.message : 'Search failed')
          setHits([])
        }
      }
    }, 300)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [query, resolvedMode])

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const trimmed = query.trim()
    const destination = resolvedMode === 'personalities' ? '/personality-records' : '/browse'
    onSearch?.(trimmed)
    navigate(trimmed ? `${destination}?q=${encodeURIComponent(trimmed)}` : destination)
    setOpen(false)
  }

  const placeholder =
    resolvedMode === 'personalities'
      ? 'Search personalities'
      : resolvedMode === 'content'
        ? 'Search content'
        : 'Search Everything'

  return (
    <form onSubmit={handleSubmit} className="relative flex w-full items-center">
      <Input
        ref={inputRef}
        value={query}
        onChange={(event) => {
          const nextQuery = event.target.value
          setQuery(nextQuery)
          if (!nextQuery.trim()) {
            setHits([])
            setSearchError(null)
          }
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        className={large ? 'font-body h-14 rounded-[22px] border-crusta-800/24 bg-white pl-5 pr-14 text-base font-semibold text-crusta-950 placeholder:text-crusta-600 shadow-[0_16px_36px_rgba(155,43,17,0.18)]' : 'font-body rounded-xl border-crusta-800/20 bg-white pr-12 font-semibold text-crusta-950 placeholder:text-crusta-600 shadow-[0_12px_30px_rgba(155,43,17,0.14)]'}
      />
      <button
        type="submit"
        aria-label="Search"
        className="absolute right-2 flex h-10 w-10 items-center justify-center rounded-xl text-[#ff5e63] transition-colors hover:bg-crusta-50"
      >
        <SiteIcon name="search" className="h-5 w-5" />
      </button>
      {open && query.trim() ? (
        <div className="absolute left-0 right-0 top-[calc(100%+0.5rem)] z-50 overflow-hidden rounded-[20px] border border-crusta-800/18 bg-crusta-950/95 shadow-[0_24px_80px_rgba(155,43,17,0.24)] backdrop-blur-xl">
          <BrandStar tone="soft" className="absolute right-4 top-4 h-4 w-4 opacity-80" />
          {searchError ? (
            <div className="space-y-3 p-5 text-crusta-50">
              <p className="font-bold text-crusta-300">Search error</p>
              <p className="text-sm text-[#c8d7f5]">{searchError}</p>
            </div>
          ) : hits.length ? (
            <div className="divide-y divide-white/10">
              {hits.map((hit) => (
                <Link
                  key={`${hit.kind}-${hit.id}`}
                  to={hit.href}
                  target={hit.href.startsWith('http') ? '_blank' : undefined}
                  rel={hit.href.startsWith('http') ? 'noreferrer' : undefined}
                  onClick={() => setOpen(false)}
                  className="font-body block px-5 py-3 text-crusta-50 hover:bg-white/10"
                >
                  <p className="font-body text-xs font-bold uppercase tracking-[0.14em] text-crusta-300">{hit.kind}</p>
                  <p className="mt-1 text-base font-bold">{hit.label}</p>
                  <p className="text-sm text-[#c8e6c9]">{hit.meta}</p>
                </Link>
              ))}
            </div>
          ) : (
            <div className="space-y-3 p-5 text-crusta-50">
              <p className="font-bold">Nothing indexed for "{query.trim()}" yet.</p>
              {resolvedMode === 'personalities' ? (
                <Button asChild size="sm">
                  <Link to={`/dashboard?submit=${encodeURIComponent(query.trim())}`}>Be first to submit a personality</Link>
                </Button>
              ) : (
                <Button asChild size="sm">
                  <Link to={`/browse?q=${encodeURIComponent(query.trim())}`}>Search full content results</Link>
                </Button>
              )}
            </div>
          )}
        </div>
      ) : null}
    </form>
  )
}
